import { Module } from "vuex";

import * as firebase from "firebase/app";
import "firebase/firestore";
import moment from "moment";
import { CommentSenderRoleEnum } from "@/enums/CommentSenderRoleEnum";
import { batchUpdate } from "@/utils/batchUpdate";
import { localizedDate } from "@/utils/localizedDate";
import { cutString } from "@/utils/stringFormat";

const messagesModule: Module<any, any> = {
  namespaced: false,
  state: {
    residentMessagesMonth: [],
    familyMessages: [],
    familyMemberStaffChats : []
  },
  mutations: {
    setResidentMessages(state: any, payload: []) {
      state.residentMessagesMonth = payload;
    },
    setFamilyMessages(state: any, payload: []) {
      state.familyMessages = payload;
    },
    setFamilyMemberStaffChats(state: any, payload: []) {
      state.familyMemberStaffChats = payload;
    },
  },
  actions: {
    async getResidentMessages({ commit }, { residentId, duration }) {
      const fromDate = Number(
        moment(new Date())
          .subtract(duration, 'days')
          .format('x')
      );

      const residentMessage = firebase
        .firestore()
        .collection('messages')
        .where('facilityId', '==', this.state.authModule.currentFacility)
        .where('residentId', '==', residentId)
        .where('createdAt', '>=', fromDate);

      await residentMessage.get().then(querySnapshot => {
        const resList: Array<any> = [];
        querySnapshot.docs.forEach(x => {
          const thisRes = x.data();
          thisRes.id = x.id;
          thisRes.start = moment(thisRes.createdAt).toDate();
          resList.push(thisRes);
        });
        commit('setResidentMessages', resList);
      });
    },
    async getFamilyMessages({ commit }, { familyId, duration }) {
      let fromDate = null;
      if (duration) {
        fromDate = Number(
          moment()
            .subtract(duration, 'days')
            .format('x')
        );
      }

      let query = firebase
        .firestore()
        .collection('messages')
        .where('facilityId', '==', this.state.authModule.currentFacility)
        .where('familyHubUserId', '==', familyId);

      if (fromDate !== null) {
        query = query.where('createdAt', '>=', fromDate).orderBy('createdAt');
      } else {
        query = query.orderBy('createdAt');
      }

      const querySnapshot = await query.get();
      const resList = querySnapshot.docs.map(doc => {
        const data = doc.data();
        data.id = doc.id;
        data.createdAtDisplay = localizedDate(data.createdAt);
        data.createdAtDisplay = moment(data.createdAt).isSame(moment(), 'day')
          ? localizedDate(
              data.createdAt,
              this.state.authModule.currentFacilityData.timeFormat
            )
          : localizedDate(data.createdAt);
        return data;
      });

      commit('setFamilyMessages', resList);
      return resList
    },
    async getFamilyMemberStaffChats({ commit }, familyHubUserId) {
      const currentUser = this.state.authModule.user;
      const familyChats = firebase
        .firestore()
        .collection('chats')
        .where('facilityId', '==', this.state.authModule.currentFacility)
        .where('familyUser.id', '==', familyHubUserId)
        .orderBy('createdAt', 'desc');

      await familyChats.get().then(querySnapshot => {
        const deletedUserLabel = 'Deleted user';
        const resList: Array<any> = [];
        querySnapshot.docs.forEach(x => {
          try {
            const thisRes = x.data();
            thisRes.id = x.id;
            if (!thisRes.staffUser) {
              thisRes.staffUser = { id: null, name: deletedUserLabel, image: '' };
            }
            thisRes.staffUserName = thisRes.staffUser.name || deletedUserLabel;
            thisRes.lastTimeOfContact = thisRes.lastMessage?.createdAt
              ? localizedDate(thisRes.lastMessage.createdAt)
              : '';
            thisRes.lastTimeOfContactDate = thisRes.lastMessage?.createdAt
              ? moment(thisRes.lastMessage.createdAt)
              : moment(0);
            thisRes.lastMessageText = cutString(thisRes.lastMessage?.text || '', 30);
            thisRes.createdAtDisplay = thisRes.createdAt
              ? localizedDate(thisRes.createdAt)
              : '';
            resList.push(thisRes);
          } catch (err) {
            console.warn(`Skipping chat ${x.id} due to invalid data:`, err);
          }
        });

        resList.sort(
          (a, b) => a.lastTimeOfContactDate - b.lastTimeOfContactDate
        );

        const currentUserChatIndex = resList.findIndex(
          chat => chat.staffUser?.id === currentUser.id
        );
        if (currentUserChatIndex !== -1) {
          const [currentUserChat] = resList.splice(currentUserChatIndex, 1);
          resList.unshift(currentUserChat);
        }

        commit('setFamilyMemberStaffChats', resList);
      });
    },
    async updateLastMessageInFamilyStaffChat({ commit, state }, data) {
      const chats = state.familyMemberStaffChats || [];

      const chatIndex = chats.findIndex(x => x.channel === data.channel);

      if (chatIndex === -1) {
        console.warn(`Chat with channel ${data.channel} not found`);
        return; 
      }

      const updatedChat = { ...chats[chatIndex], lastMessageText: data.message.text, lastMessage: data.message };

      const updatedChats = [
        ...chats.slice(0, chatIndex),
        updatedChat,
        ...chats.slice(chatIndex + 1)
      ];

      commit('setFamilyMemberStaffChats', updatedChats);
    },
    async readIncomingFamilyMessages({ commit }, channel) {
      try {
        const snap = await firebase
          .firestore()
          .collection('messages')
          .where('facilityId', '==', this.state.authModule.currentFacility)
          .where('read', '==', false)
          .where('channel', '==', channel)
          .get();

        const itemsToUpdate = snap.docs.map(n => {
          const itemToUpdate = {
            docId: n.id,
            updates: { read: true, readDateTime: new Date() },
          };
          return itemToUpdate;
        });

        const result = await batchUpdate('messages', itemsToUpdate);

        const chatSnap = await firebase.firestore().collection('chats')
          .where('facilityId', '==', this.state.authModule.currentFacility)
          .where('channel', '==', channel)
          .limit(1)
          .get();

        if (chatSnap.empty) {
          throw new Error('Chat not found for facility/channel')
        }

        const chatDoc = chatSnap.docs[0];
        const chatDocId = chatSnap.docs[0].id
        const data = chatDoc.data();

        if (data?.lastMessage && typeof data.lastMessage === 'object') {
          await firebase.firestore().collection('chats').doc(chatDocId).update({
            "lastMessage.read": true,
            "lastMessage.readDateTime": new Date()
          })
        }

        if (result.status === 200) return true;
        else return false;
      } catch (error) {
        return false;
      }
    },
    async readIncomingFamilyMessage({ commit }, messageId) {
      try {
        const snap = await firebase
          .firestore()
          .collection('messages')
          .doc(messageId)
          .get();

        const messageData = snap.data()

        if (messageData?.to !== CommentSenderRoleEnum.Staff) {
          return false;
        }

        snap.ref.update({ read: true, readDateTime: moment().toISOString() });

        const chatSnap = await firebase.firestore().collection('chats')
          .where('facilityId', '==', this.state.authModule.currentFacility)
          .where('channel', '==', messageData.channel)
          .limit(1)
          .get();

        if (chatSnap.empty) {
          throw new Error('Chat not found for facility/channel')
        }

        const chatDoc = chatSnap.docs[0];
        const chatDocId = chatSnap.docs[0].id
        const data = chatDoc.data();

        if (data?.lastMessage && typeof data.lastMessage === 'object') {
          await firebase.firestore().collection('chats').doc(chatDocId).update({
            "lastMessage.read": true,
            "lastMessage.readDateTime": new Date()
          })
        }

        return true;
      } catch (error) {
        return false;
      }
    },
    async createMessage({ dispatch, commit, state }, data) {
      try {
        data.facilityId = this.state.authModule.currentFacility;
        data.createdAt = new Date().toISOString();
        const res = await firebase.firestore().collection('messages').add(data);
        data.id = res.id;

        dispatch('pushMessageTofamilyMessagesStore', data);

        return data;
      } catch (error) {
        console.error(error);
        return null;
      }
    },
    async pushMessageTofamilyMessagesStore({ dispatch, commit, state }, message) {
      try {
        const familyMessages = state.familyMessages
         message.createdAtDisplay = localizedDate(message.createdAt);
         message.createdAtDisplay = moment(message.createdAt).isSame(moment(), 'day')
          ? localizedDate(
              message.createdAt,
              this.state.authModule.currentFacilityData.timeFormat
            )
          : localizedDate(message.createdAt);

        familyMessages.push(message);
        commit('setFamilyMessages', familyMessages);
        console.log('pushMessageTofamilyMessagesStore: ', message);

        return message;
      } catch (error) {
        console.error(error);
        return null;
      }
    },
    async pushChatToStoreStore({ dispatch, commit, state }, chatData) {
      try {
        const familyMemberStaffChats = state.familyMemberStaffChats
        const deletedUserLabel = 'Deleted user';

        const safeStaffUser = chatData.staffUser
          || { id: null, name: deletedUserLabel, image: '' };

        const chat = {
            id: chatData.id,
            createdAtDisplay: chatData.createdAt
              ? (moment(chatData.createdAt).isSame(moment(), 'day')
                  ? localizedDate(chatData.createdAt, this.state.authModule.currentFacilityData.timeFormat)
                  : localizedDate(chatData.createdAt))
              : '',
            staffUserName: safeStaffUser.name || deletedUserLabel,
            lastTimeOfContact: chatData.lastMessage?.createdAt
              ? localizedDate(chatData.lastMessage.createdAt)
              : '',
            lastTimeOfContactDate: chatData.lastMessage?.createdAt
              ? moment(chatData.lastMessage.createdAt)
              : moment(0),
            lastMessageText: cutString(chatData.lastMessage?.text || '', 30),
          ...chatData,
          staffUser: safeStaffUser,
        }

        familyMemberStaffChats.push(chat);
        commit('setFamilyMemberStaffChats', familyMemberStaffChats);
        console.log('pushChatToStoreStore: ', chat);

        return chat;
      } catch (error) {
        console.error(error);
        return null;
      }
    }
  },
};

export default messagesModule;